'use client';

import type { PropsWithChildren } from 'react';
import { createContext, useCallback, useMemo } from 'react';
import type {
  PlanSuggestion,
  PlanSuggestionInfoFragment,
} from '@frameio/graphql-client/generated';
import {
  useGetCanManageBillingPermissionQuery,
  useGetFeatureGateSuggestionsQuery,
  useGetShareFeatureGateSuggestionsQuery,
} from '@frameio/graphql-client/generated';
import { useActiveAccountId, useActiveShareId } from '@frameio/shared/contexts';

export type FeatureGateSuggestion = PlanSuggestionInfoFragment &
  Partial<Pick<PlanSuggestion, 'disabledInCurrentAccount'>>;

export type GetSuggestion = (
  feature: string
) => FeatureGateSuggestion | undefined;

// Two contexts so suggestion readers don't re-render when only the loading
// flag changes (and vice versa). Both default to safe "no data" values so
// readers rendered outside a provider treat every feature as unavailable.
export const GetSuggestionContext = createContext<GetSuggestion>(
  () => undefined
);
GetSuggestionContext.displayName = 'FeatureGatesGetSuggestionContext';

export const FeatureGatesLoadingContext = createContext<boolean>(false);
FeatureGatesLoadingContext.displayName = 'FeatureGatesLoadingContext';

type ProviderProps = PropsWithChildren<{
  mode: 'app' | 'share-review';
}>;

export function FeatureGatesContextProvider({ mode, children }: ProviderProps) {
  const accountId = useActiveAccountId();
  const shareId = useActiveShareId();
  const isApp = mode === 'app';

  const { data: accountData, loading: accountLoading } =
    useGetFeatureGateSuggestionsQuery({
      variables: { accountId: accountId ?? '' },
      skip: !isApp || !accountId,
    });

  const { loading: billingLoading } = useGetCanManageBillingPermissionQuery({
    variables: { accountId: accountId ?? '' },
    skip: !isApp || !accountId,
  });

  const { data: shareData, loading: shareLoading } =
    useGetShareFeatureGateSuggestionsQuery({
      variables: { shareId: shareId ?? '' },
      skip: isApp || !shareId,
    });

  const suggestions = isApp
    ? accountData?.account?.subscription?.suggestedPlan
    : shareData?.share?.suggestedPlan;

  const map = useMemo(() => {
    const next = new Map<string, FeatureGateSuggestion>();
    for (const suggestion of suggestions ?? []) {
      if (suggestion?.feature)
        next.set(suggestion.feature.toLowerCase(), suggestion);
    }
    return next;
  }, [suggestions]);

  const getSuggestion = useCallback<GetSuggestion>(
    (feature) => map.get(feature.toLowerCase()),
    [map]
  );

  const loading = isApp ? accountLoading || billingLoading : shareLoading;

  return (
    <GetSuggestionContext.Provider value={getSuggestion}>
      <FeatureGatesLoadingContext.Provider value={loading}>
        {children}
      </FeatureGatesLoadingContext.Provider>
    </GetSuggestionContext.Provider>
  );
}
